import * as React from 'react';

import { QuickChallengeCard } from '@/features/bhanzu-buddy/components/quick-challenge-card';
import { cn } from '@/utils';

import { getRandomChallengeQuestionWithMeta } from '../utils/quick-challenge-questions/quick-challenge-utils';

import { ClosedViewDesktop } from './closed-view-desktop';
import { ClosedViewMobile } from './closed-view-mobile';

interface ClosedViewProps {
  position: { x: number; y: number };
  containerRef: React.Ref<HTMLDivElement>;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleTouchStart: (e: React.TouchEvent) => void;
  isDragging: boolean;
  wasDragged: boolean;
  handleToggleOpen: () => void;
  message: string;
  setMessage: (message: string) => void;
  handleKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder: string;
  disabled: boolean;
  handleVoiceRecord: () => void;
  isListening: boolean;
  speechError: string | null;
  handleSendMessage: () => void;
  showQuickChallenge: {
    open: boolean;
    question: string;
    category: string;
    color: string;
  };
  setShowQuickChallenge: (state: {
    open: boolean;
    question: string;
    category?: string;
    color?: string;
  }) => void;
  handleQuickChallengeClose: () => void;
  quickChallengeContextRef: React.RefObject<{
    questionByBB: string;
  } | null>;
  appendBuddyMessage?: (message: string) => void;
  selectedProfile?: { grade?: string };
  chatHistoryQuery: { data?: { interactions?: any[] } };
  ref: React.ForwardedRef<HTMLDivElement>;
}

export const ClosedView = ({
  position,
  containerRef,
  handleMouseDown,
  handleTouchStart,
  isDragging,
  wasDragged,
  handleToggleOpen,
  message,
  setMessage,
  handleKeyPress,
  placeholder,
  disabled,
  handleVoiceRecord,
  isListening,
  speechError,
  handleSendMessage,
  showQuickChallenge,
  setShowQuickChallenge,
  handleQuickChallengeClose,
  quickChallengeContextRef,
  appendBuddyMessage,
  selectedProfile,
  chatHistoryQuery,
  ref,
}: ClosedViewProps) => {
  return (
    <div
      ref={node => {
        if (typeof containerRef === 'function') {
          containerRef(node);
        } else if (containerRef) {
          containerRef.current = node;
        }
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      }}
      className={cn('fixed z-(--z-bhanzu-buddy)', 'md:w-[630px] sm:w-72')}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      role="button"
      tabIndex={0}
      aria-label="Drag to move BhanzuBuddy"
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const button = e.currentTarget.querySelector('button');
          button?.click();
        }
      }}
    >
      {showQuickChallenge.open ? (
        <div
          className={cn(
            'absolute z-10',
            'bg-magical-moonlight-d3 rounded-2xl p-3',
            'animate-in slide-in-from-bottom-4 duration-500 ease-out',
            'md:w-[630px] sm:w-72 w-auto'
          )}
          style={{ bottom: '100%' }}
          data-node-id="1501:6566"
        >
          <QuickChallengeCard
            question={showQuickChallenge.question}
            category={showQuickChallenge.category}
            color={showQuickChallenge.color}
            setShowQuickChallenge={setShowQuickChallenge}
            setCurrentElement={() => {}}
            onClose={handleQuickChallengeClose}
            onQuestionClick={() => {
              setShowQuickChallenge({ open: false, question: '' });
              const qcQuestion = showQuickChallenge.question;
              quickChallengeContextRef.current = { questionByBB: qcQuestion };
              handleToggleOpen();
              setTimeout(() => {
                appendBuddyMessage?.(qcQuestion);
              }, 0);
            }}
            onShuffle={() => {
              const questionData = getRandomChallengeQuestionWithMeta(
                selectedProfile?.grade,
                chatHistoryQuery.data?.interactions
              );
              setShowQuickChallenge({
                open: true,
                question: questionData.text,
                category: questionData.category,
                color: questionData.color,
              });
              quickChallengeContextRef.current = {
                questionByBB: questionData.text,
              };
            }}
          />
        </div>
      ) : null}

      <ClosedViewMobile
        isDragging={isDragging}
        wasDragged={wasDragged}
        handleToggleOpen={handleToggleOpen}
      />

      <ClosedViewDesktop
        isDragging={isDragging}
        wasDragged={wasDragged}
        handleToggleOpen={handleToggleOpen}
        message={message}
        setMessage={setMessage}
        handleKeyPress={handleKeyPress}
        placeholder={placeholder}
        disabled={disabled}
        handleVoiceRecord={handleVoiceRecord}
        isListening={isListening}
        speechError={speechError}
        handleSendMessage={handleSendMessage}
      />
    </div>
  );
};
